/* eslint-disable sonarjs/cognitive-complexity */
import { ReactElement } from 'react';

import { Icon, Icons } from '../Icon';

import { Avatar as StyledAvatar } from './Avatar.styled';
import { Badge as StyledBadge } from './Badge.styled';
import { Editable as StyledEditable } from './Editable.styled';
import { AvatarProps, BackgroundColor, PresenceKey, Response } from './types';

const backgroundColorOptions = 9;

export const Avatar = ({
  backgroundColor,
  badgeContent,
  badgeProps,
  children,
  isEditable,
  presence,
  response,
  size = 56,
  onEdit,
  ...props
}: AvatarProps) => {
  const presenceTitlesMap: { [s: string]: string } = {
    away: 'Away',
    busy: 'Busy',
    busyOnCall: 'Busy',
    busyOnMeeting: 'Busy',
    offline: 'Offline',
    online: 'Online',
    dnd: 'DND',
  };
  const iconsByResponse: { [s: string]: ReactElement } = {
    maybe: <Icon icon={Icons.question} sx={{ fontSize: 10 }} />,
    no: <Icon icon={Icons.close} sx={{ fontSize: 10 }} />,
    yes: <Icon icon={Icons.check} sx={{ fontSize: 10 }} />,
  };

  const iconsByPresence: { [s: string]: ReactElement } = {
    away: <Icon icon={Icons.clockSolid} />,
    busy: <Icon icon={Icons.faCircleSolid} />,
    busyOnCall: <Icon icon={Icons.circlePhoneSolid} />,
    busyOnMeeting: <Icon icon={Icons.circleVideoSolid} />,
    offline: <Icon icon={Icons.offline} />,
    online: <Icon icon={Icons.checkCircleSolid} />,
    dnd: <Icon icon={Icons.dnd} />,
  };

  const background: BackgroundColor =
    typeof children === 'string' &&
    children.length <= 2 &&
    children.length > 0 &&
    backgroundColor === undefined
      ? children.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % backgroundColorOptions
      : backgroundColor;

  const statusInTitle = presence
    ? presenceTitlesMap[typeof presence === 'string' ? presence : presence.presenceKey]
    : '';
  const messageKeyInTitle =
    typeof presence === 'string' || !presence?.messageKey ? '' : ' ' + presence?.messageKey;
  const customMessageInTitle =
    typeof presence === 'string' || !presence?.customMessage ? '' : ', ' + presence?.customMessage;
  const title = `${statusInTitle}${messageKeyInTitle}${customMessageInTitle}`;
  return (
    <StyledBadge
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      avatarSize={size}
      badgeContent={
        badgeContent ||
        iconsByResponse[response as Response] ||
        iconsByPresence[
          typeof presence === 'string' ? (presence as PresenceKey) : (presence?.presenceKey as PresenceKey)
        ]
      }
      className={iconsByResponse[response as Response] ? 'response-badge' : statusInTitle}
      invisible={!(badgeContent || response || presence)}
      overlap="circular"
      presence={presence}
      response={response}
      showZero
      title={title}
      {...badgeProps}
    >
      <StyledAvatar backgroundColor={background} size={size} {...props}>
        {children || (!props.src && <Icon icon={Icons.userSolid} sx={{ fontSize: 'inherit' }} />)}
      </StyledAvatar>
      {isEditable && (
        <StyledEditable avatarSize={size} onClick={onEdit}>
          <Icon icon={Icons.pen} sx={{ fontSize: size / 2.5 }} />
        </StyledEditable>
      )}
    </StyledBadge>
  );
};

export default Avatar;
